import { Form, FormContext } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { triggerEvent } from 'src/js/events';
import { AudioEncoderEditForm } from './audio-encoder-edit-form';
import { AudioAlgorithmEnum, AudioEncoderViewModel, audioEncoderValidationSchema } from './audio-encoder-models';
import audioEncoders, { updateAudioEncoder } from './audio-encoder-store';
import { useMutable } from 'src/js/hook/use-mutable';
import { MkRange } from '../video-encoder/video-encoder-models';

interface Props {
  model: AudioEncoderViewModel;
  onCancel: VoidFunction;
}

export const AudioEncoderEdit: React.FunctionComponent<Props> = ({ model, onCancel }) => {
  const { t } = useTranslation();

  const formRef = useRef<FormikProps<AudioEncoderViewModel>>();
  const [settings, setSettings] = useState<AudioEncoderViewModel>(model); // once the form is opened dont spuriously update its values
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const [bitRateRange, setBitRateRange] = useMutable<MkRange>({ min: 5, max: 2016 });

  const handleSubmit = (values: AudioEncoderViewModel) => {
    if (values.algorithm === AudioAlgorithmEnum.RAW) {
      //If we change back to ADTS or LOAS, then the bitrate should be reverted to the last configured acceptable value
      values.bitRate = settings.bitRate;
    }
    setButtonState('pending');
    return updateAudioEncoder(values, t).then((res) => {
      if (res.ok) {
        const encoder = audioEncoders.find((e) => e.id === model.id);
        setSettings(encoder); // update edit view with newest model
        formRef.current.resetForm({ values: encoder }); // FIXME somehow the rest after this is not executed
        triggerEvent('preset-update');
      }
      setButtonState(undefined);
      return res;
    });
  };

  return (
    <Form
      initialValues={settings}
      defaultValidation={true}
      handleSubmit={handleSubmit}
      className="hai-mt-n8 mk-audio-edit-form"
      restValidationProps={{
        enableReinitialize: true,
        validationSchema: audioEncoderValidationSchema(t, bitRateRange),
        innerRef: formRef,
      }}
    >
      <FormContext.Consumer>
        {(formContext: FormikProps<AudioEncoderViewModel>) => {
          return (
            <AudioEncoderEditForm
              applyButtonState={buttonState}
              getBitRateRange={bitRateRange}
              formikProps={formContext}
              onCancel={onCancel}
              setBitRateRange={setBitRateRange}
            />
          );
        }}
      </FormContext.Consumer>
    </Form>
  );
};
